import { type EventType } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useState } from 'react'
import { View } from 'react-native'
import Icon, { type IconName } from '~/components/Icon'
import { Label } from '~/components/typography/Content'
import { type ButtonSize, type ButtonVariant } from '~/components/ui/button'
import { Calendar } from '~/components/ui/calendar'
import { InlineEditSheet } from '~/components/ui/InlineEditSheet'
import { Text } from '~/components/ui/text'
import { ToggleButton } from '~/components/ui/toggle-button'
import { useUpdateEvent } from '~/data/events/useEvents'
import { cn } from '~/lib/utils'

type AddDateInlineProps = {
  currentDate?: Date
  currentEventType?: EventType
  eventId: string
  onSuccess?: () => void
  className?: string
  triggerClassName?: string
  triggerVariant?: ButtonVariant
  triggerSize?: ButtonSize
  triggerLabel: string
}

export function AddDateInline({
  currentDate,
  currentEventType,
  eventId,
  onSuccess,
  className,
  triggerClassName,
  triggerVariant = 'outline',
  triggerSize = 'default',
  triggerLabel,
}: AddDateInlineProps) {
  const { t } = useTranslations('events')

  const EVENT_TYPES: { value: EventType; label: string; icon: IconName }[] = [
    {
      value: 'showing',
      label: t(($) => $.eventTypes.showing),
      icon: 'eye-outline',
    },
    {
      value: 'appraisal',
      label: t(($) => $.eventTypes.appraisal),
      icon: 'clipboard-outline',
    },
  ]
  const [date, setDate] = useState<Date | undefined>(currentDate)
  const [eventType, setEventType] = useState<EventType | undefined>(currentEventType)
  const { mutate: updateEvent, isPending } = useUpdateEvent()

  const handleSave = () => {
    updateEvent(
      {
        eventId,
        eventDate: date,
        eventType,
      },
      {
        onSuccess: () => {
          onSuccess?.()
        },
      }
    )
  }

  const handleCancel = () => {
    setDate(currentDate)
    setEventType(currentEventType)
  }

  return (
    <InlineEditSheet
      modalKey={`add-date-${eventId}`}
      title={t(($) => $.addDateInline.title)}
      triggerLabel={triggerLabel}
      onSave={handleSave}
      onCancel={handleCancel}
      isSaving={isPending}
      isDisabled={isPending}
      className={className}
      triggerClassName={triggerClassName}
      triggerVariant={triggerVariant}
      triggerSize={triggerSize}>
      <View className="gap-6">
        <View className="gap-2">
          <Label className="text-center">{t(($) => $.addDateInline.eventType)}</Label>
          <View className="flex-row gap-2">
            {EVENT_TYPES.map((type) => (
              <ToggleButton
                key={type.value}
                value={eventType === type.value}
                onValueChange={() => setEventType(type.value)}
                className={cn(
                  'flex-1 flex-row items-center justify-center gap-2 rounded-xl border border-border px-4 py-3 transition-colors duration-150 aria-selected:border-accent aria-selected:bg-accent/10 active:bg-muted'
                )}>
                <Icon
                  name={type.icon}
                  size={20}
                  className={cn('text-secondary group-aria-selected/button:text-accent')}
                />
                <Text
                  className={cn(
                    'font-medium text-secondary group-aria-selected/button:text-accent'
                  )}>
                  {type.label}
                </Text>
              </ToggleButton>
            ))}
          </View>
        </View>

        <View className="gap-2">
          <Label className="text-center">{t(($) => $.addDateInline.selectDateAndTime)}</Label>
          <Calendar
            date={date ?? new Date()}
            onChange={setDate}
            timePicker
            minDate={new Date()}
          />
        </View>
      </View>
    </InlineEditSheet>
  )
}
